import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { proxied } from "@/lib/feed";

export const ViewerMedia = ({ post, onEdge, startAtEnd }) => {
  const media = post.media?.length ? post.media : [{ image_url: post.image_url }];
  const [slide, setSlide] = useState(startAtEnd ? media.length - 1 : 0);
  const videoRef = useRef(null);
  const touch = useRef(null);
  const item = media[Math.min(slide, media.length - 1)];

  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    const dir = dx < 0 ? 1 : -1;
    const next = slide + dir;
    if (next >= 0 && next < media.length) setSlide(next);
    else onEdge(dir);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {
      v.muted = true;
      v.play().catch(() => {});
    });
  }, [item.video_url]);

  return (
    <div
      data-testid="viewer-media"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="relative flex items-center justify-center bg-black h-full min-h-0 overflow-hidden touch-pan-y select-none"
    >
      {item.video_url ? (
        <video
          key={item.video_url}
          ref={videoRef}
          data-testid="viewer-video"
          src={item.video_url}
          poster={proxied(item.image_url)}
          controls
          playsInline
          className="max-h-full max-w-full"
        />
      ) : (
        <img data-testid="viewer-image" src={proxied(item.image_url)} alt={post.caption.slice(0, 80) || "Instagram post"} className="max-h-full max-w-full object-contain" />
      )}
      {media.length > 1 && (
        <>
          {[["prev", -1, ChevronLeft, "left-3"], ["next", 1, ChevronRight, "right-3"]].map(([dir, d, Icon, pos]) => (
            <button
              key={dir}
              data-testid={`viewer-slide-${dir}-btn`}
              onClick={() => setSlide((s) => s + d)}
              disabled={dir === "prev" ? slide === 0 : slide === media.length - 1}
              aria-label={dir === "prev" ? "Previous image" : "Next image"}
              className={`absolute top-1/2 -translate-y-1/2 ${pos} flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-md border border-white/15 transition-[opacity,background-color] duration-300 hover:bg-[var(--volt)] hover:text-[var(--ink)] disabled:opacity-0 disabled:pointer-events-none`}
            >
              <Icon className="h-5 w-5" />
            </button>
          ))}
          <span data-testid="viewer-slide-counter" className="absolute top-3 right-3 rounded-full bg-black/60 px-3 py-1 font-mono text-[11px] text-white backdrop-blur-md">
            {slide + 1} / {media.length}
          </span>
        </>
      )}
      {media.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex max-w-full">
          {media.map((_, i) => (
            <button
              key={i}
              data-testid={`viewer-slide-${i}`}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className="flex h-11 min-w-6 sm:min-w-11 items-center justify-center px-1 sm:px-1.5"
            >
              <span className={`block h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === slide ? "w-6 bg-[var(--volt)]" : "w-1.5 bg-white/40"}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
